import { useEffect, useMemo, useState } from "react";
import { ChartLine, Lightning, Drop, Ruler, Anchor, Fire, Truck, Bell, Gear, Trophy, MapPin } from "@phosphor-icons/react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const EQUIP_META = {
  jumbo:       { label: "Jumbo",       icon: Ruler,   tone: "bg-blue-50 text-blue-900",       accent: "#1E3A8A" },
  anclador:    { label: "Bolter",      icon: Anchor,  tone: "bg-amber-50 text-amber-900",     accent: "#B45309" },
  voladuras:   { label: "Voladuras",   icon: Fire,    tone: "bg-red-50 text-red-900",         accent: "#DC2626" },
  scoop:       { label: "Scoop tram",  icon: Truck,   tone: "bg-emerald-50 text-emerald-900", accent: "#059669" },
  camion:      { label: "Camión BP",   icon: Truck,   tone: "bg-slate-100 text-slate-900",    accent: "#475569" },
  servicios:   { label: "Servicios",   icon: Lightning,tone: "bg-purple-50 text-purple-900",  accent: "#7C3AED" },
  telehandler: { label: "Telehandler", icon: Truck,   tone: "bg-cyan-50 text-cyan-900",       accent: "#0891B2" },
  retro:       { label: "Retro",       icon: Truck,   tone: "bg-orange-50 text-orange-900",   accent: "#EA580C" },
  motoniv:     { label: "Motoniv",     icon: Truck,   tone: "bg-lime-50 text-lime-900",       accent: "#4D7C0F" },
};

// Etiqueta legible + unidad + icono decorativo para cada rate key
const RATE_META = {
  m_por_hora_efectiva:     { l: "Metros / hora efectiva",   u: "m/h",  icon: Ruler },
  m_por_hora_percutida:    { l: "Metros / hora percusión",  u: "m/h",  icon: Lightning },
  m_por_hora_electrica:    { l: "Metros / hora eléctrica",  u: "m/h",  icon: Lightning },
  barrenos_por_hora:       { l: "Barrenos / hora",          u: "brn/h", icon: Ruler },
  rendimiento_barra_m:     { l: "Rendimiento de barra 16ft",u: "m/pza", icon: Ruler },
  pernos_por_hora:         { l: "Pernos / hora",            u: "pz/h",  icon: Anchor },
  pernos_por_hora_percutida:{ l: "Pernos / hora percusión", u: "pz/h",  icon: Lightning },
  malla_por_hora_m2:       { l: "Malla / hora",             u: "m²/h",  icon: Anchor },
  pernos_por_broca:        { l: "Rendimiento por broca",    u: "pz/broca", icon: Anchor },
  pernos_por_barra:        { l: "Rendimiento por barra",    u: "pz/barra", icon: Anchor },
  rendimiento_broca_m_por_pieza: { l: "Rendimiento broca",  u: "m/pza", icon: Anchor },
  rendimiento_barra_m_por_pieza: { l: "Rendimiento barra",  u: "m/pza", icon: Ruler },
  ton_por_hora:            { l: "Toneladas / hora",         u: "t/h",   icon: Truck },
  ciclos_por_hora:         { l: "Ciclos / hora",            u: "c/h",   icon: Truck },
  ton_por_litro_diesel:    { l: "Toneladas / L diésel",     u: "t/L",   icon: Drop },
  ciclos_por_litro_diesel: { l: "Ciclos / L diésel",        u: "c/L",   icon: Drop },
  lt_diesel_por_ton:       { l: "Diésel / tonelada",        u: "L/t",   icon: Drop },
  ton_por_km:              { l: "Toneladas / km",           u: "t/km",  icon: Truck },
  lt_diesel_por_km:        { l: "Diésel / km",              u: "L/km",  icon: Drop },
  factor_carga_kg_por_m:   { l: "Factor de carga",          u: "kg/m",  icon: Fire },
  kg_por_barreno:          { l: "Explosivo / barreno",      u: "kg/brn", icon: Fire },
  m_por_barreno:           { l: "Avance / barreno",         u: "m/brn",  icon: Ruler },
  avance_por_disparo:      { l: "Avance / disparo",         u: "m/disp", icon: Fire },
  m_por_hora:              { l: "Metros avance / hora",     u: "m/h",   icon: Ruler },
  m_instalados_por_hora:   { l: "Metros instalados / hora", u: "m/h",   icon: Ruler },
  m_instalados_por_turno:  { l: "M instalados / turno",     u: "m",     icon: Ruler },
  servicios_por_hora:      { l: "Servicios / hora",         u: "s/h",   icon: Lightning },
  horas_maquina:           { l: "Horas máquina (diesel)",   u: "h",     icon: Truck },
  horas_por_turno:         { l: "Horas efectivas / turno",  u: "h",     icon: Truck },
  diesel_por_hora:         { l: "Diésel / hora",            u: "L/h",   icon: Drop },
  area_por_hora_m2:        { l: "Área nivelada / hora",     u: "m²/h",  icon: Ruler },
  km_por_hora:             { l: "Kilómetros / hora",        u: "km/h",  icon: Truck },
  m3_por_hora:             { l: "m³ / hora",                u: "m³/h", icon: Ruler },
  ton_por_ciclo:           { l: "Ton / ciclo",              u: "t",    icon: Truck },
  ton_por_turno:           { l: "Ton / turno",              u: "t",    icon: Truck },
  servicios_por_turno:     { l: "Servicios / turno",        u: "s",    icon: Lightning },
  area_por_turno_m2:       { l: "Área / turno",             u: "m²",   icon: Ruler },
  km_por_turno:            { l: "Km / turno",               u: "km",   icon: Truck },
  anclas_por_hora:         { l: "Anclas / hora",            u: "pz/h", icon: Anchor },
};

// Unidad de la métrica primaria por tipo de equipo (para ranking)
const PRIMARY_UNIT = {
  jumbo: "m", anclador: "pz", voladuras: "m", scoop: "t", camion: "t",
  servicios: "m", telehandler: "m²", retro: "m²", motoniv: "m²",
};

const isoDaysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

export default function OperationsKpis() {
  const [df, setDf] = useState(isoDaysAgo(29));
  const [dt, setDt] = useState(isoDaysAgo(0));
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);        // rango consultado
  const [ref, setRef] = useState(null);          // referencia 30 días previos
  const [scanBusy, setScanBusy] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [cfg, setCfg] = useState({ threshold_pct: 20, responsible: "Jefe de Operaciones" });
  const [cfgBusy, setCfgBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [{ data: main }, { data: prev }] = await Promise.all([
        api.get(`/operations/kpis?date_from=${df}&date_to=${dt}`),
        api.get(`/operations/kpis?date_from=${isoDaysAgo(59)}&date_to=${isoDaysAgo(30)}`),
      ]);
      setData(main);
      setRef(prev);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const runScan = async (dryRun = false) => {
    setScanBusy(true);
    try {
      const { data: r } = await api.post("/operations/kpi-alerts/scan", {
        date_from: df, date_to: dt, dry_run: dryRun,
      });
      setScanResult(r);
      if (dryRun) {
        toast.info(`Simulación: se generarían ${r.created_count} alerta(s)`);
      } else if (r.created_count > 0) {
        toast.success(`✓ ${r.created_count} alerta(s) HSE creada(s) para ${r.responsible}`);
      } else {
        toast.info("Sin caídas de rendimiento por encima del umbral 👍");
      }
    } catch (e) { toast.error(apiError(e)); }
    finally { setScanBusy(false); }
  };

  const openConfig = async () => {
    try {
      const { data: c } = await api.get("/operations/kpi-alerts/config");
      setCfg(c);
    } catch (_e) { /* usar default */ }
    setConfigOpen(true);
  };

  const saveConfig = async () => {
    setCfgBusy(true);
    try {
      const { data: c } = await api.put("/operations/kpi-alerts/config", cfg);
      setCfg(c);
      toast.success("Configuración guardada ✓");
      setConfigOpen(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setCfgBusy(false); }
  };

  useEffect(() => { load(); }, []);

  const equipmentList = useMemo(() => {
    if (!data) return [];
    return Object.entries(data.by_equipment).sort((a, b) => (b[1].entries || 0) - (a[1].entries || 0));
  }, [data]);

  const trendPct = (curr, prev) => {
    if (!prev || prev === 0) return null;
    return Math.round(((curr - prev) / prev) * 100);
  };

  return (
    <div className="space-y-5" data-testid="ops-kpis-panel">
      <div className="flex flex-wrap items-end gap-3 bg-white border border-slate-200 rounded-lg p-4">
        <div>
          <Label className="overline-label">Desde</Label>
          <Input type="date" value={df} onChange={(e) => setDf(e.target.value)} data-testid="kpis-from" className="w-40" />
        </div>
        <div>
          <Label className="overline-label">Hasta</Label>
          <Input type="date" value={dt} onChange={(e) => setDt(e.target.value)} data-testid="kpis-to" className="w-40" />
        </div>
        <Button onClick={load} disabled={busy} data-testid="kpis-load" className="bg-blue-900 hover:bg-blue-800 gap-2">
          <ChartLine size={14} weight="bold" /> {busy ? "Cargando…" : "Recalcular"}
        </Button>
        <Button onClick={() => runScan(false)} disabled={scanBusy || !data} data-testid="kpis-scan-alerts" variant="outline" className="gap-2 border-amber-300 text-amber-900 hover:bg-amber-50">
          <Bell size={14} weight="bold" /> {scanBusy ? "Escaneando…" : "Escanear alertas"}
        </Button>
        <Button onClick={openConfig} variant="ghost" size="icon" data-testid="kpis-alert-config" title="Configurar alertador">
          <Gear size={16} weight="bold" />
        </Button>
        {data && (
          <p className="text-xs text-slate-500 ml-auto">
            {data.entries_count} registros · vs. promedio de los 30 días anteriores
          </p>
        )}
      </div>

      {scanResult && scanResult.created_count > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 space-y-2" data-testid="kpis-scan-result">
          <p className="text-sm font-heading font-bold text-amber-900 flex items-center gap-2">
            <Bell size={16} weight="fill" className="text-amber-700" />
            {scanResult.created_count} alerta(s) creada(s) · asignada(s) a {scanResult.responsible}
          </p>
          <ul className="text-xs text-amber-800 space-y-1 pl-6 list-disc">
            {scanResult.alerts_created.slice(0, 6).map((a) => (
              <li key={a.id}>{a.description}</li>
            ))}
            {scanResult.alerts_created.length > 6 && (
              <li className="italic">+ {scanResult.alerts_created.length - 6} más — revisa el módulo HSE.</li>
            )}
          </ul>
        </div>
      )}

      {!data ? (
        <p className="text-sm text-slate-400 text-center py-16">Selecciona un rango y presiona Recalcular.</p>
      ) : equipmentList.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-16" data-testid="kpis-empty">No hay registros en el rango seleccionado.</p>
      ) : (
        equipmentList.map(([eq, k]) => {
          const meta = EQUIP_META[eq] || { label: eq, icon: ChartLine, tone: "bg-slate-100 text-slate-700", accent: "#334155" };
          const Icon = meta.icon;
          const refRates = ref?.by_equipment?.[eq]?.rates || {};
          const rates = k.rates || {};
          return (
            <section key={eq} className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={`kpis-section-${eq}`}>
              <header className={`flex items-center gap-3 px-5 py-3 ${meta.tone}`}>
                <div className="h-9 w-9 rounded-md bg-white/70 flex items-center justify-center">
                  <Icon size={18} weight="duotone" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading font-bold text-base tracking-tight">{meta.label}</h3>
                  <p className="text-xs opacity-70">
                    {k.entries} turno(s) · {k.hours_efectivas} h ef. · Disp. {k.availability_pct}%
                    {k.hours_percusion > 0 && ` · ${k.hours_percusion} h perc.`}
                    {k.diesel_lt > 0 && ` · ${k.diesel_lt} L diésel`}
                  </p>
                </div>
              </header>

              <div className="p-5 space-y-4">
                {/* Grid de KPIs derivados */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {Object.entries(rates).map(([rk, rv]) => {
                    const rmeta = RATE_META[rk] || { l: rk, u: "", icon: ChartLine };
                    const t = trendPct(rv, refRates[rk]);
                    return (
                      <div key={rk} className="border border-slate-200 rounded-md p-3 flex items-start gap-2.5" data-testid={`kpi-${eq}-${rk}`}>
                        <rmeta.icon size={18} weight="duotone" className="text-slate-500 mt-0.5 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold leading-tight">{rmeta.l}</p>
                          <p className="font-mono font-black text-xl text-slate-900 leading-none mt-1">
                            {rv.toLocaleString("es-MX", { maximumFractionDigits: 2 })}
                            <span className="text-[10px] font-semibold text-slate-400 ml-1">{rmeta.u}</span>
                          </p>
                          {t !== null && (
                            <p className={`text-[10px] font-bold mt-1 ${t > 0 ? "text-emerald-700" : t < 0 ? "text-red-600" : "text-slate-400"}`}>
                              {t > 0 ? "▲" : t < 0 ? "▼" : "="} {Math.abs(t)}% vs. 30d prev.
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Gráfica de tendencia diaria */}
                {k.series && k.series.length > 1 && (
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={k.series} margin={{ top: 6, right: 12, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 10 }} />
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        {eq === "jumbo" || eq === "voladuras" ? (
                          <>
                            <Line type="monotone" dataKey="metros" name="Metros" stroke={meta.accent} strokeWidth={2} dot={false} />
                            <Line type="monotone" dataKey="avance" name="Avance" stroke="#DC2626" strokeWidth={2} dot={false} />
                          </>
                        ) : eq === "anclador" ? (
                          <>
                            <Line type="monotone" dataKey="pernos" name="Pernos" stroke={meta.accent} strokeWidth={2} dot={false} />
                            <Line type="monotone" dataKey="malla" name="Malla m²" stroke="#7C3AED" strokeWidth={2} dot={false} />
                          </>
                        ) : eq === "scoop" || eq === "camion" ? (
                          <>
                            <Line type="monotone" dataKey="ton" name="Toneladas" stroke={meta.accent} strokeWidth={2} dot={false} />
                            <Line type="monotone" dataKey="diesel" name="Diésel L" stroke="#EA580C" strokeWidth={2} dot={false} />
                          </>
                        ) : eq === "servicios" ? (
                          <Line type="monotone" dataKey="hours_ef" name="Horas ef." stroke={meta.accent} strokeWidth={2} dot={false} />
                        ) : (
                          <>
                            <Line type="monotone" dataKey="hours_ef" name="Horas ef." stroke={meta.accent} strokeWidth={2} dot={false} />
                            <Line type="monotone" dataKey="diesel" name="Diésel L" stroke="#EA580C" strokeWidth={2} dot={false} />
                          </>
                        )}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Rankings: top operadores + top áreas */}
                {((k.operator_ranking && k.operator_ranking.length > 0) || (k.area_ranking && k.area_ranking.length > 0)) && (
                  <div className="grid md:grid-cols-2 gap-3 pt-2 border-t border-slate-100">
                    {k.operator_ranking && k.operator_ranking.length > 0 && (
                      <div data-testid={`kpi-${eq}-operators`}>
                        <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1.5 flex items-center gap-1">
                          <Trophy size={12} weight="bold" className="text-amber-600" /> Top operadores
                        </p>
                        <table className="w-full text-[11px]">
                          <thead className="text-[9px] uppercase text-slate-400">
                            <tr>
                              <th className="text-left">#</th>
                              <th className="text-left">Operador</th>
                              <th className="text-right">Producción</th>
                              <th className="text-right">Turnos</th>
                              <th className="text-right">Horas ef.</th>
                            </tr>
                          </thead>
                          <tbody>
                            {k.operator_ranking.map((op, i) => (
                              <tr key={op.operator_id} className="border-t border-slate-100">
                                <td className="text-slate-400 font-bold">{i + 1}</td>
                                <td className="font-semibold text-slate-800 truncate max-w-[160px]" title={op.name}>{op.name}</td>
                                <td className="text-right tabular-nums font-black text-slate-900">
                                  {op.primary.toLocaleString("es-MX", { maximumFractionDigits: 2 })}
                                  <span className="text-[9px] text-slate-400 ml-1">{PRIMARY_UNIT[eq] || ""}</span>
                                </td>
                                <td className="text-right tabular-nums text-slate-600">{op.entries}</td>
                                <td className="text-right tabular-nums text-slate-600">{op.hours_ef}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {k.area_ranking && k.area_ranking.length > 0 && (
                      <div data-testid={`kpi-${eq}-areas`}>
                        <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1.5 flex items-center gap-1">
                          <MapPin size={12} weight="bold" className="text-blue-700" /> Top áreas / labores
                        </p>
                        <table className="w-full text-[11px]">
                          <thead className="text-[9px] uppercase text-slate-400">
                            <tr>
                              <th className="text-left">#</th>
                              <th className="text-left">Área</th>
                              <th className="text-right">Producción</th>
                              <th className="text-right">Turnos</th>
                              <th className="text-right">Horas ef.</th>
                            </tr>
                          </thead>
                          <tbody>
                            {k.area_ranking.map((ar, i) => (
                              <tr key={ar.area} className="border-t border-slate-100">
                                <td className="text-slate-400 font-bold">{i + 1}</td>
                                <td className="font-semibold text-slate-800 truncate max-w-[160px]" title={ar.area}>{ar.area}</td>
                                <td className="text-right tabular-nums font-black text-slate-900">
                                  {ar.primary.toLocaleString("es-MX", { maximumFractionDigits: 2 })}
                                  <span className="text-[9px] text-slate-400 ml-1">{PRIMARY_UNIT[eq] || ""}</span>
                                </td>
                                <td className="text-right tabular-nums text-slate-600">{ar.entries}</td>
                                <td className="text-right tabular-nums text-slate-600">{ar.hours_ef}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </section>
          );
        })
      )}

      {/* Config del alertador */}
      <Dialog open={configOpen} onOpenChange={setConfigOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Bell size={18} weight="duotone" className="text-amber-700" /> Alertador automático de rendimiento
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-600 leading-relaxed">
              Cuando un KPI cae más del umbral configurado respecto al promedio de los 30 días previos,
              el sistema crea automáticamente una acción correctiva HSE (prioridad alta, vencimiento +7 días)
              asignada al responsable indicado. Las alertas duplicadas se evitan con un `source_id` idempotente.
            </p>
            <div>
              <Label className="overline-label">Umbral de caída (%)</Label>
              <Input
                type="number" min="1" max="90" step="1"
                data-testid="alert-threshold-input"
                value={cfg.threshold_pct}
                onChange={(e) => setCfg({ ...cfg, threshold_pct: Number(e.target.value) })}
              />
              <p className="text-[10px] italic text-slate-500 mt-1">Recomendado: 20%. Ejemplo: si el m/h del Jumbo cayó 25% vs. 30d previos, se crea alerta.</p>
            </div>
            <div>
              <Label className="overline-label">Responsable asignado</Label>
              <Input
                data-testid="alert-responsible-input"
                value={cfg.responsible}
                onChange={(e) => setCfg({ ...cfg, responsible: e.target.value })}
                placeholder="Jefe de Operaciones"
              />
            </div>
            <div className="pt-2 border-t border-slate-100">
              <Button onClick={() => runScan(true)} disabled={scanBusy || !data} variant="outline" size="sm" className="w-full gap-1.5" data-testid="alert-dry-run">
                Simular escaneo (sin crear acciones)
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfigOpen(false)}>Cancelar</Button>
            <Button onClick={saveConfig} disabled={cfgBusy} data-testid="alert-config-save" className="bg-blue-900 hover:bg-blue-800">
              {cfgBusy ? "Guardando…" : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
